/**
 * DATEV-artige Debitoren-Nummernkreise: je Anfangsbuchstabe ein Bereich,
 * Sch/St wie im Telefonbuch mit eigenen Bereichen. Die autoritative Vergabe
 * laeuft ueber die RPC next_customer_number (race-sicher); hier liegen nur
 * Klassifikation und pure Vorschau-/Fallback-Logik.
 */

export interface NumberRange {
    key: string;
    start: number;
    end: number;
}

export const DEBITOR_RANGES: NumberRange[] = [
    { key: 'A', start: 30000, end: 30399 },
    { key: 'B', start: 30400, end: 30799 },
    { key: 'C', start: 30800, end: 31199 },
    { key: 'D', start: 31200, end: 31599 },
    { key: 'E', start: 31600, end: 31999 },
    { key: 'F', start: 32000, end: 32399 },
    { key: 'G', start: 32400, end: 32799 },
    { key: 'H', start: 32800, end: 33199 },
    { key: 'I', start: 33200, end: 33399 },
    { key: 'J', start: 33400, end: 33599 },
    { key: 'K', start: 33600, end: 33999 },
    { key: 'L', start: 34000, end: 34399 },
    { key: 'M', start: 34400, end: 34799 },
    { key: 'N', start: 34800, end: 35199 },
    { key: 'O', start: 35200, end: 35599 },
    { key: 'P', start: 35600, end: 35999 },
    { key: 'Q', start: 36000, end: 36099 },
    { key: 'R', start: 36100, end: 36499 },
    { key: 'S', start: 36500, end: 36899 },
    { key: 'Sch', start: 36900, end: 37299 },
    { key: 'St', start: 37300, end: 37699 },
    { key: 'T', start: 37700, end: 38099 },
    { key: 'U', start: 38100, end: 38499 },
    { key: 'V', start: 38500, end: 38899 },
    { key: 'W', start: 38900, end: 39299 },
    { key: 'X', start: 39300, end: 39399 },
    { key: 'Y', start: 39400, end: 39499 },
    { key: 'Z', start: 39500, end: 39999 },
];

/** Kreditoren = Debitoren-Bereich + 60000 (90000–99999). Noch ungenutzt. */
export const KREDITOR_OFFSET = 60000;

const UMLAUTS: Record<string, string> = { ä: 'a', ö: 'o', ü: 'u' };

export const rangeForName = (name: string): NumberRange | null => {
    const lower = (name || '')
        .toLowerCase()
        .replace(/[äöü]/g, (m) => UMLAUTS[m]);
    // Erster Buchstabe irgendwo im Namen ("3B Bau" -> B); keiner -> manuell.
    const idx = lower.search(/[a-z]/);
    if (idx < 0) return null;
    const rest = lower.slice(idx);
    const key = rest.startsWith('sch')
        ? 'Sch'
        : rest.startsWith('st')
            ? 'St'
            : rest[0].toUpperCase();
    return DEBITOR_RANGES.find((r) => r.key === key) ?? null;
};

/** Kleinste freie Nummer im Bereich — pure Vorschau; autoritativ ist die RPC. */
export const nextFreeInRange = (
    range: NumberRange,
    taken: Iterable<string>,
): string | null => {
    const used = new Set<number>();
    for (const t of taken) {
        // Nur rein numerische Nummern belegen — gleiche Regel wie
        // customer_number ~ '^\d+$' in SQL. parseInt wuerde sonst
        // Praefixe schlucken ("30000-ALT" belegt faelschlich 30000).
        const raw = (t ?? '').trim();
        if (!/^\d+$/.test(raw)) continue;
        used.add(Number.parseInt(raw, 10));
    }
    for (let n = range.start; n <= range.end; n++) {
        if (!used.has(n)) return String(n);
    }
    return null;
};
